'use client';

import { useRunnerRequestUiRuntime } from '../../../runtime';

import type { ReactNode } from 'react';
import { createElement } from 'react';
import { NodeViewWrapper, type NodeViewProps } from '@tiptap/react';
import { useCallback, useMemo } from 'react';
import type { EntityType } from './EntityReference';
import {
  BitbucketIcon,
  GitBranchIcon,
  GitHubIcon,
  GitLabIcon,
  LinearIcon,
  SentryIcon,
} from '../../../components/icons/ProviderIcons';

const svgIcon = (path: string, color: string) =>
  createElement(
    'span',
    { className: `inline-flex h-4 w-4 items-center justify-center rounded ${color}` },
    createElement(
      'svg',
      {
        xmlns: 'http://www.w3.org/2000/svg',
        className: 'h-3 w-3',
        fill: 'none',
        viewBox: '0 0 24 24',
        stroke: 'currentColor',
      },
      createElement('path', { strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2', d: path }),
    ),
  );

export const entityConfig: Partial<Record<EntityType, { icon: string | ReactNode; external?: boolean }>> = {
  convention: {
    icon: svgIcon(
      'M12 6.253v13m0-13C10.832 5.483 9.246 5 7.5 5 4.462 5 2 6.567 2 8.5v10C2 16.567 4.462 15 7.5 15c1.746 0 3.332.483 4.5 1.253m0-10C13.168 5.483 14.754 5 16.5 5c3.038 0 5.5 1.567 5.5 3.5v10c0-1.933-2.462-3.5-5.5-3.5-1.746 0-3.332.483-4.5 1.253',
      'text-info bg-info/10',
    ),
  },
  completionReport: {
    icon: svgIcon(
      'M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
      'text-accent bg-accent/10',
    ),
  },
  postMortem: {
    icon: svgIcon(
      'M9 17v-2m3 2v-4m3 4V7m2 12H7a2 2 0 01-2-2V5a2 2 0 012-2h7.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V17a2 2 0 01-2 2z',
      'text-warning bg-warning/10',
    ),
  },
  coAction: {
    icon: svgIcon(
      'M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z',
      'text-secondary bg-secondary/10',
    ),
  },
  codeReview: {
    icon: svgIcon(
      'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.031 9-11.622 0-1.042-.133-2.052-.382-3.016z',
      'text-success bg-success/10',
    ),
  },
  document: {
    icon: svgIcon(
      'M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5A3.375 3.375 0 0010.125 2.25H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z',
      'text-neutral bg-neutral/10',
    ),
  },
  skill: {
    icon: svgIcon('M13 10V3L4 14h7v7l9-11h-7z', 'text-secondary bg-secondary/10'),
  },
  plan: {
    icon: svgIcon(
      'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
      'text-primary bg-primary/10',
    ),
  },
  // 러너 요청 프롬프트에서 플랜 칩 바로 아래에 놓이므로, 플랜(primary)과 구분되는 토큰을 쓴다.
  // accent는 completionReport와 겹친다 — 시맨틱 토큰(primary·secondary·accent·info·success·warning·
  // neutral) 7종을 엔티티 9종이 나눠 쓰고 있어 남는 토큰이 없다(coAction·skill이 secondary를 공유하는
  // 것과 같은 상황). 색은 인접 칩과의 구분까지만 책임지고, 종류 구분은 아이콘이 담당한다.
  planTask: {
    icon: svgIcon('M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z', 'text-accent bg-accent/10'),
  },
  GITHUB_ISSUE: { icon: createElement(GitHubIcon, { className: 'inline h-3.5 w-3.5' }), external: true },
  GITHUB_PR: { icon: createElement(GitHubIcon, { className: 'inline h-3.5 w-3.5' }), external: true },
  GITLAB_ISSUE: { icon: createElement(GitLabIcon, { className: 'inline h-3.5 w-3.5' }), external: true },
  GITLAB_MERGE_REQUEST: { icon: createElement(GitLabIcon, { className: 'inline h-3.5 w-3.5' }), external: true },
  BITBUCKET_ISSUE: { icon: createElement(BitbucketIcon, { className: 'inline h-3.5 w-3.5' }), external: true },
  BITBUCKET_PR: { icon: createElement(BitbucketIcon, { className: 'inline h-3.5 w-3.5' }), external: true },
  LINEAR_ISSUE: { icon: createElement(LinearIcon, { className: 'inline h-3.5 w-3.5' }), external: true },
  SENTRY_ISSUE: { icon: createElement(SentryIcon, { className: 'inline h-3.5 w-3.5' }), external: true },
  branch: { icon: createElement(GitBranchIcon, { className: 'inline h-3 w-3 text-primary' }) },
};

export const EntityReferenceView = (props: NodeViewProps) => {
  const attrs = props.node.attrs as {
    entityType: EntityType;
    entityId: string;
    label: string;
    htmlUrl?: string;
  };

  const { buildEntityGoHref } = useRunnerRequestUiRuntime();
  const config = useMemo(() => entityConfig[attrs.entityType] ?? entityConfig.convention!, [attrs.entityType]);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (!props.editor.isEditable && attrs.entityId.length > 0) {
        if (config.external && attrs.htmlUrl) {
          window.open(attrs.htmlUrl, '_blank', 'noopener,noreferrer');
          return;
        }

        const href = buildEntityGoHref(attrs.entityType, attrs.entityId);
        if (!href) {
          return;
        }

        window.open(href, '_blank', 'noopener,noreferrer');
      }
    },
    [props.editor.isEditable, attrs.entityType, attrs.entityId, attrs.htmlUrl, config.external, buildEntityGoHref],
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      props.deleteNode();
    },
    [props],
  );

  return (
    <NodeViewWrapper as="span" className="inline">
      <span
        role="button"
        tabIndex={0}
        className="group inline-flex cursor-pointer items-center gap-1 rounded-md bg-primary/10 px-1.5 py-0.5 text-sm font-medium text-primary transition-colors hover:bg-primary/20 max-w-full"
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleClick(e as unknown as React.MouseEvent);
          }
        }}
      >
        <span className="shrink-0 select-none">{config.icon}</span>
        <span className="truncate">{attrs.label}</span>
        {props.editor.isEditable ? (
          <button
            type="button"
            className="ml-0.5 hidden rounded px-0.5 text-xs text-primary/60 hover:bg-primary/20 hover:text-primary group-hover:inline-flex"
            onClick={handleDelete}
            aria-label="Remove reference"
          >
            &times;
          </button>
        ) : null}
      </span>
    </NodeViewWrapper>
  );
};
